import {
  DisconnectOutlined,
  EllipsisOutlined,
  HistoryOutlined,
  StarOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { Button, Col, notification, Row } from "antd";
import { useState } from "react";
import styled from "styled-components";

import { PERMISSION_SCOPES, useAuth } from "@/modules/auth";
import { useAssignTaxCaseMutation } from "../../../../../api/graphql/mutations/taxCase/assignTaxCase.generated";
import { useUnassignTaxCaseMutation } from "../../../../../api/graphql/mutations/taxCase/unassignTaxCase.generated";
import { TaxBoardUser } from "../../../../../api/graphql/schema.generated";

import { destroyMessage, showLoadingMessage } from "../../../../../utils";
import ActionLogDrawerAsync from "../../../../common/ActionLogDrawerAsync";
import UserInfoExcerpt from "../../../../common/UserInfoExcerpt";
import { UserInfoDrawerSources } from "../../../../common/UserInfoExcerpt/UserInfoDrawer";
import { useKontaxUserContext } from "../../../../contexts/KontaxUserContext";
import AssignTaxCaseModal from "./AssignTaxCaseModal";

const Container = styled.div`
  margin-bottom: 30px;
`;

const LOADING_MESSAGE_KEY = "assigning-or-unassigning-tax-case";

const getTaxCaseAssignee = (taxBoardUser: TaxBoardUser) =>
  taxBoardUser.kontaxUser?.taxCases[0]?.assignee;

const TaxCaseActions = ({
  taxBoardUser,
  onRefresh,
}: {
  taxBoardUser: TaxBoardUser;
  onRefresh: () => void;
}) => {
  const { getOperatorId, hasOperatorPermissionScope } = useAuth();
  const { user, notes, isLoading } = useKontaxUserContext();
  const [assignTaxCase, { loading: isAssigning }] = useAssignTaxCaseMutation();
  const [unassignTaxCase, { loading: isUnassigning }] =
    useUnassignTaxCaseMutation();
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const canAssignOnBehalf = hasOperatorPermissionScope([
    PERMISSION_SCOPES.TAX_SUPERVISOR,
    PERMISSION_SCOPES.ADMIN,
  ]);

  // `assignee` is the operator id (SMT-963), so compare against the acting
  // operator's id, not their email.
  const currentOperatorId = getOperatorId();

  const assignee = getTaxCaseAssignee(taxBoardUser);
  const taxCaseId = taxBoardUser.kontaxUser?.taxCases[0]?.id!;

  const handleAssignUnassignButton = async () => {
    showLoadingMessage(LOADING_MESSAGE_KEY);
    try {
      if (getTaxCaseAssignee(taxBoardUser)) {
        await unassignTaxCase({ variables: { taxCaseId } });
      } else {
        await assignTaxCase({ variables: { taxCaseId } });
      }
      // Refetch the board so the row's Bearbeiter column reflects the new
      // assignment (the mutation updates the normalized cache, but the table's
      // memoized list doesn't re-derive from that on its own).
      onRefresh();
    } catch (error) {
      notification.error({
        message: `Error occurred while assign or unassign client`,
      });
    } finally {
      destroyMessage(LOADING_MESSAGE_KEY);
    }
  };

  return (
    <Container>
      <Row gutter={8}>
        <Col>
          <Button
            type={!assignee ? "default" : "primary"}
            icon={
              !assignee || assignee === currentOperatorId ? (
                <StarOutlined />
              ) : (
                <DisconnectOutlined />
              )
            }
            onClick={handleAssignUnassignButton}
            loading={isAssigning || isUnassigning}
          >
            {!assignee
              ? "Mandant übernehmen"
              : assignee === currentOperatorId
              ? "Mir zugewiesen"
              : "Mandant abgeben"}
          </Button>
        </Col>
        {canAssignOnBehalf && taxCaseId && (
          <Col>
            <Button
              type="default"
              icon={<UserSwitchOutlined />}
              onClick={() => setIsAssignModalOpen(true)}
            >
              Mandant zuweisen
            </Button>
          </Col>
        )}
        <Col>
          {taxCaseId && (
            <ActionLogDrawerAsync
              title="Action log"
              modelName="tax_case"
              recordIds={[taxCaseId!]}
              renderButton={({ onClick }) => (
                <Button icon={<HistoryOutlined />} onClick={onClick}>
                  View logs
                </Button>
              )}
            />
          )}
        </Col>
        <Col>
          {user && (
            <UserInfoExcerpt
              user={user}
              notes={notes}
              source={UserInfoDrawerSources.TAX}
              isLoading={isLoading}
              renderExcerptData={() => {
                return (
                  <Button type="default" icon={<EllipsisOutlined />}>
                    More info
                  </Button>
                );
              }}
            />
          )}
        </Col>
      </Row>
      {canAssignOnBehalf && taxCaseId && (
        <AssignTaxCaseModal
          taxCaseId={taxCaseId}
          open={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          onSuccess={onRefresh}
        />
      )}
    </Container>
  );
};

export default TaxCaseActions;
